import axios from 'axios';
import { normalizeMsisdn } from '../utils/msisdn';

const axiosClient = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

let isRefreshing = false;
let failedQueue = [];

const MAX_RATE_LIMIT_RETRIES = 2;

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    })

    failedQueue = [];
}

// Request Interceptor: Add token to headers
axiosClient.interceptors.request.use(
    (config) => {
        const token = sessionStorage.getItem('access_token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response Interceptor: Handle 401 and token refresh
axiosClient.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (!originalRequest || !error.response) {
            return Promise.reject(error);
        }

        // Rate limited: back off and retry a couple of times
        if (error.response.status === 429) {
            originalRequest._rateLimitRetries = (originalRequest._rateLimitRetries || 0) + 1;
            if (originalRequest._rateLimitRetries > MAX_RATE_LIMIT_RETRIES) {
                return Promise.reject(error);
            }

            const retryAfter = parseFloat(error.response.headers?.['retry-after']);
            const backoff = Number.isFinite(retryAfter)
                ? retryAfter * 1000
                : 1000 * Math.pow(2, originalRequest._rateLimitRetries - 1) + Math.random() * 300;

            await sleep(backoff);
            return axiosClient(originalRequest);
        }

        // Check if it's a 401 error and we haven't already retried
        if (error.response.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((token) => {
                    originalRequest._retry = true;
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    return axiosClient(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            let msisdn = sessionStorage.getItem('msisdn');
            let refferer = sessionStorage.getItem('refferer');
            if (!refferer) {
                const urlParams = new URLSearchParams(window.location.search);
                refferer = urlParams.get('refferer') || urlParams.get('referer');
            }

            if (!msisdn) {
                const urlParams = new URLSearchParams(window.location.search);
                const msisdnFromUrl = normalizeMsisdn(urlParams.get('a'));
                if (msisdnFromUrl) {
                    msisdn = msisdnFromUrl;
                    sessionStorage.setItem('msisdn', msisdn);
                } else {
                    // console.error("MSISDN not found in sessionStorage or URL.");
                    return Promise.reject(new Error("MSISDN not found"));
                }
            }

            try {
                let refreshResponse;
                if(refferer === "gamenow") {
                    refreshResponse = await axios.post(`${process.env.REACT_APP_BASE_URL}/v1/jt/gen`, { msisdn, refferer });
                } else {
                    refferer = null;
                    // console.log("No Ref")
                    refreshResponse = await axios.post(`${process.env.REACT_APP_BASE_URL}/v1/jt/gen`, { msisdn, refferer });
                }
                const newToken = refreshResponse.headers['access_token'];
                if (newToken) {
                    sessionStorage.setItem('access_token', newToken);
                    axiosClient.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
                    originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
                    processQueue(null, newToken);
                    window.dispatchEvent(new CustomEvent('tokenChanged'));
                    return axiosClient(originalRequest);
                } else {
                    throw new Error("No token received from refresh endpoint");
                }
            } catch (refreshError) {
                // console.error('Token refresh failed:', refreshError);
                processQueue(refreshError, null);
                sessionStorage.removeItem('access_token');
                sessionStorage.removeItem('msisdn');
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // console.error('API Error:', error);
        return Promise.reject(error);
    }
);

export default axiosClient;